import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Palette } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const DesignerRegistration = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    full_name: '',
    email: '',
    phone: '',
    portfolio_url: '',
    design_specialization: '',
    experience_years: '',
    design_description: '',
    social_media: ''
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/designer/register`, formData);
      
      toast({
        title: "Application Submitted!",
        description: "Your designer registration will be reviewed by our team. We'll contact you within 3-5 business days.",
      });
      
      setTimeout(() => {
        navigate('/');
      }, 2000);
    } catch (error) {
      toast({
        title: "Submission Failed",
        description: error.response?.data?.detail || "Please try again later.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto">
          <Card className="p-6 md:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-red-600 rounded-lg flex items-center justify-center">
                <Palette className="h-6 w-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl md:text-3xl font-bold">Designer Registration</h1>
                <p className="text-gray-600">Submit your designs and earn lifetime royalties</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-900">
                <strong>What You Get:</strong> Full credit on products, lifetime royalties, global reach, and designer support.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="full_name">Full Name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  value={formData.full_name}
                  onChange={handleChange}
                  required
                  placeholder="Your full name"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="email">Email Address *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="your@email.com"
                  />
                </div>
                <div>
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    placeholder="+91 9876543210"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="portfolio_url">Portfolio URL *</Label>
                <Input
                  id="portfolio_url"
                  name="portfolio_url"
                  type="url"
                  value={formData.portfolio_url}
                  onChange={handleChange}
                  required
                  placeholder="https://yourportfolio.com or Behance/Dribbble link"
                />
                <p className="text-xs text-gray-500 mt-1">Share your Behance, Dribbble, or personal portfolio</p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="design_specialization">Design Specialization *</Label>
                  <Input
                    id="design_specialization"
                    name="design_specialization"
                    value={formData.design_specialization}
                    onChange={handleChange}
                    required
                    placeholder="e.g., Product Design, Industrial Design"
                  />
                </div>
                <div>
                  <Label htmlFor="experience_years">Years of Experience *</Label>
                  <Input
                    id="experience_years"
                    name="experience_years"
                    type="number"
                    value={formData.experience_years}
                    onChange={handleChange}
                    required
                    min="0"
                    placeholder="e.g., 5"
                  />
                </div>
              </div>

              <div>
                <Label htmlFor="design_description">Tell us about your design style *</Label>
                <Textarea
                  id="design_description"
                  name="design_description"
                  value={formData.design_description}
                  onChange={handleChange}
                  required
                  rows={4}
                  placeholder="Describe your design philosophy, style, and what makes your work unique..."
                />
              </div>

              <div>
                <Label htmlFor="social_media">Social Media (Optional)</Label>
                <Input
                  id="social_media"
                  name="social_media"
                  value={formData.social_media}
                  onChange={handleChange}
                  placeholder="Instagram, LinkedIn, or other social profiles"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <Button type="button" variant="outline" onClick={() => navigate('/')} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700">
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DesignerRegistration;
